<template>
  <div class="v2-analytics-page">
    <!--
      Internal analytics dashboard. Gated by the
      `kp_3080_internal_analytics` Flagsmith flag — when on, we render the
      already-implemented internal dashboard from `views/apps/analytics`
      (KPI cards, filters, orders chart, popular products, sales by
      retailer). The legacy Analytics component owns its own
      analytics.track calls for OPENS/CLICKS/EXITS, so V2Analytics skips
      them in this branch to avoid duplicate events.
    -->
    <analytics v-if="hasInternalAnalytics" />

    <!--
      Makeke iframe fallback (flag off). Keeps the original V2 behavior —
      load the Makeke URL via the catalog `integrations.makeke` endpoint
      and render it in an iframe. Only path that should call that API.
    -->
    <template v-else>
      <div
        v-if="!isLoadedAnalyticsUrl"
        class="v2-page-loading"
      >
        <v2-spinner
          size="lg"
          label="Loading analytics..."
        />
      </div>

      <div v-else>
        <iframe
          v-resize="{ log: true }"
          class="v2-analytics-page__iframe"
          title="Makeke Dashboard"
          width="100%"
          :src="makekeDashboardUrl"
        />
      </div>
    </template>
  </div>
</template>

<script>
import V2Spinner from '@/components/v2/V2Spinner.vue'
import Analytics from '@/views/apps/analytics/Analytics.vue'
import { useAnalytics } from '@/composables/useAnalytics'
import { FLAG_CONSTANTS } from '@/constants'

export default {
  name: 'V2Analytics',
  components: {
    V2Spinner,
    Analytics,
  },
  setup() {
    const {
      makekeDashboardUrl,
      isLoadedAnalyticsUrl,
      getAnalyticsUrl,
      trackClicksAnalytics,
      trackExitAnalytics,
    } = useAnalytics()

    return {
      makekeDashboardUrl,
      isLoadedAnalyticsUrl,
      getAnalyticsUrl,
      trackClicksAnalytics,
      trackExitAnalytics,
    }
  },
  computed: {
    hasInternalAnalytics() {
      return this.$store.getters['flags/hasFeature'](FLAG_CONSTANTS.KP_3080_INTERNAL_ANALYTICS)
    },
  },
  watch: {
    hasInternalAnalytics: {
      immediate: false,
      handler(val) {
        // Flagsmith may resolve after mount. If the flag arrives as `false`
        // post-mount, we still need to fetch the Makeke URL so the iframe
        // can render. The created() guard handles the synchronous case.
        if (!val) this.getAnalyticsUrl()
      },
    },
  },
  created() {
    if (this.hasInternalAnalytics) {
      // Internal-analytics path: the embedded <analytics> child owns the
      // BRAND_CLICKS_ANALYTICS / BRAND_OPENS_ANALYTICS_PAGE tracking and
      // does not need a Makeke URL. Don't call the previous Makeke API
      // here — that's exactly the "don't directly call the previous api"
      // case the flag is meant to gate.
      return
    }
    this.trackClicksAnalytics()
    this.getAnalyticsUrl()
  },
  destroyed() {
    // When the embedded <analytics> rendered, it already fires
    // BRAND_EXITS_ANALYTICS_PAGE on its own destroyed hook. Only fire it
    // here for the Makeke fallback path.
    if (!this.hasInternalAnalytics) {
      this.trackExitAnalytics()
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-analytics-page {
  // Glass panel — wraps the Makeke Dashboard iframe in the same glass
  // surface used elsewhere so the page reads as part of the v2 system.
  // The iframe content itself is third-party and not styled by us.
  background: var(--v2-glass-surface-1);
  backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-lg);
  box-shadow: var(--v2-glass-shadow);
  padding: var(--v2-space-3);
  overflow: hidden;

  &__iframe {
    border: 0;
    display: block;
    border-radius: var(--v2-radius-md);
    background: var(--v2-card);
  }
}
</style>
